import React, { Fragment, useRef, useContext, useCallback, useEffect } from 'react'
import styled from 'styled-components'
import { useDrag, useDrop } from 'react-dnd'
import { useSlate, useReadOnly, ReactEditor } from 'vendor/slate-react'
import { Node, Transforms, Path } from 'vendor/slate'
import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { EditorDispatch } from 'cg/components/documents/editor/slate/reducer'
import { insertCodeTab } from 'cg/components/documents/editor/slate/plugins/code'

const CodeTabsHeaderItem = ({ attributes, children, element, editor }) => {
  const { state, dispatch } = useContext(EditorDispatch) || {}
  const { activeTabs } = state || {}
  const readOnly = useReadOnly()
  const { tabId } = element
  const path = ReactEditor.findPath(editor, element)
  const codeTabsHeaderPath = (() => {
    try {
      return Node.has(editor, path) ? Path.parent(path) : null
    } catch (error) {
      return null
    }
  })()

  const codeTabsPath = (() => {
    try {
      return codeTabsHeaderPath && Node.has(editor, codeTabsHeaderPath) ? Path.parent(codeTabsHeaderPath) : null
    } catch (error) {
      return null
    }
  })()
  const codeTabs = codeTabsPath && Node.has(editor, codeTabsPath) ? Node.get(editor, codeTabsPath) : null

  const dropRef = useRef(null)

  useEffect(() => {
    if (
      codeTabs &&
      codeTabs.children &&
      codeTabs.children[0] &&
      codeTabs.children[0].children &&
      codeTabs.children[0].children[0] &&
      codeTabs.defaultActiveTab !== codeTabs.children[0].children[0].tabId
    ) {
      Transforms.setNodes(editor, { defaultActiveTab: codeTabs.children[0].children[0].tabId }, { at: codeTabsPath })
    }
  }, [codeTabs, codeTabsPath, editor])

  // Remove orphan tab headers after undo (defer so paste/normalize can finish first).
  useEffect(() => {
    if (codeTabs && codeTabsPath) return undefined

    const frame = requestAnimationFrame(() => {
      if (!Node.has(editor, path)) return

      try {
        const headerPath = ReactEditor.findPath(editor, element)
        const tabsPath = Path.parent(Path.parent(headerPath))
        if (Node.has(editor, tabsPath) && Node.get(editor, tabsPath).type === ELEMENT_TYPES.code_tabs) {
          return
        }
      } catch (error) {
        // Header is detached from a code_tabs block.
      }

      if (Node.has(editor, path)) {
        Transforms.removeNodes(editor, { at: path })
      }
    })

    return () => cancelAnimationFrame(frame)
  }, [codeTabs, codeTabsPath, path, editor, element])

  const onClick = tabId => {
    if (codeTabs) {
      activeTabs[codeTabs.id] = tabId
      dispatch({ type: 'activateTab', payload: activeTabs })
    }
  }

  const deleteCodeTab = tabId => {
    const codeTabsHeader = codeTabsHeaderPath ? Node.get(editor, codeTabsHeaderPath) : null
    const codeTabIndex = codeTabsHeader ? codeTabsHeader.children.findIndex(i => i.tabId === tabId) : null

    if (!codeTabsHeader) return

    if (codeTabsHeader.children.length === 1) {
      // Remove entire tab block if only one tab left
      if (codeTabsPath) {
        Transforms.removeNodes(editor, { at: codeTabsPath })
      }
    } else {
      const codeTabContent = codeTabs.children.find(
        tab => tab.tabId === tabId && tab.type === ELEMENT_TYPES.code_tabs_content_item
      )
      let newTabId = tabId

      if (codeTabIndex === 0) {
        // if index is 0 select the next tab
        newTabId = codeTabsHeader.children[codeTabIndex + 1].tabId
      } else {
        // select the previous tab
        newTabId = codeTabsHeader.children[codeTabIndex - 1].tabId
      }

      onClick(newTabId)

      Transforms.removeNodes(editor, { at: path })
      Transforms.removeNodes(editor, { at: ReactEditor.findPath(editor, codeTabContent) })
    }
  }

  const insertCodeTabItem = () => {
    const codeTabContent = codeTabs.children.find(tab => tab.tabId === tabId && tab.type === ELEMENT_TYPES.code_tabs_content_item)
    insertCodeTab(editor, path, ReactEditor.findPath(editor, codeTabContent))
  }

  const [{ isOver }, drop] = useDrop({
    accept: 'CodeTabHeaderItem',
    collect: monitor => ({
      isOver: monitor.isOver()
    }),
    drop(item) {
      const atPath = ReactEditor.findPath(editor, item)
      const toPath = ReactEditor.findPath(editor, element)
      // Don't replace items with themselves
      if (!atPath || !toPath || JSON.stringify(atPath) === JSON.stringify(toPath) || atPath.length !== toPath.length) {
        return
      }

      Transforms.moveNodes(editor, {
        at: atPath,
        to: toPath
      })
    }
  })

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'CodeTabHeaderItem',
    collect: monitor => ({
      isDragging: monitor.isDragging()
    }),
    item() {
      return element
    },
    canDrag() {
      return !readOnly
    }
  })

  drop(dropRef)

  const isTabActive = activeTabs
    ? codeTabs
      ? activeTabs[codeTabs.id] === tabId
      : false
    : codeTabs
      ? codeTabs.defaultActiveTab === tabId
      : false

  return (
    <CodeTab ref={dropRef} active={isTabActive}>
      <>
        {!readOnly && (
          <CodeTabActionsWrapper contentEditable={false} style={{ userSelect: 'none' }}>
            <CodeTabActionsButton
              isDragging={isDragging}
              onClick={() => {
                deleteCodeTab(tabId)
              }}
            >
              <i className="fad fa-trash" />
            </CodeTabActionsButton>
          </CodeTabActionsWrapper>
        )}
        <CodeTabTitleContainer
          active={isTabActive}
          ref={preview}
          isOver={isOver}
          onClick={() => {
            onClick(tabId)
          }}
        >
          {!readOnly && (
            <CodeTabDragHandle ref={drag} contentEditable={false} style={{ userSelect: 'none' }} active={isTabActive}>
              <i className="fa fa-grip-vertical" />
            </CodeTabDragHandle>
          )}
          <CodeTabTitle {...attributes} active={isTabActive} readOnly={readOnly}>
            {children}
          </CodeTabTitle>
          {!readOnly && (
            <CodeTabAddItem
              onClick={() => {
                insertCodeTabItem()
              }}
              contentEditable={false}
              style={{ userSelect: 'none' }}
            >
              <i className="far fa-plus" />
            </CodeTabAddItem>
          )}
        </CodeTabTitleContainer>
      </>
    </CodeTab>
  )
}

export default CodeTabsHeaderItem

const CodeTabAddItem = styled.div`
  width: 20px;
  height: 20px;
  font-size: 13px;
  border-radius: 4px;
  position: absolute;
  cursor: pointer;
  right: -10px;
  align-items: center;
  justify-content: center;
  background: ${props => props.theme.bgLightest};
  border: 1px solid ${props => props.theme.borderColor};
  color: ${props => props.theme.textGreyTinted};
  box-shadow: 1px 1px 10px rgba(0, 0, 0, 0.05);
  -webkit-transition: 0.15s;
  transition: 0.15s;
  display: none;
  z-index: 2;
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
    color: ${props => props.theme.white};
  }
`

const CodeTabActionsWrapper = styled.div`
  display: flex;
  width: 100%;
  background: transparent;
`

const CodeTabDragHandle = styled.div`
  cursor: grab;
  display: ${props => (props.active ? 'flex' : 'none')};
  margin: 0px;
  min-width:30px;
  height:100%;
  justify-content:center;
  align-items:center;
  z-index: 2;
  color: ${props => props.theme.textGreyTintedLighter};
  i {
    font-size: 12px;
  }
}
`

const CodeTabActionsButton = styled.div`
  background: ${props => props.theme.bgLightestInverse};
  font-size: 13.5px;
  height: 21px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin: 0px 2px 5px 2px;
  color: ${props => props.theme.textGreyTintedLighter};
  border-radius: 3px;
  padding: 4px;
  width: 100%;
  display: flex;
  -webkit-transition: 0.15s;
  visibility: hidden;
  &.is-dragging {
    visibility: hidden !important;
  }
  &:hover,
  &:active {
    background: ${props => props.theme.primary};
    color: ${props => props.theme.bgSecondLightest};
    border-color: ${props => props.theme.primary};
    box-shadow: none;
  }
`

const CodeTab = styled.li`
  flex: 1;
  max-width: 90%;
  font-size: 12.5px;
  display: flex;
  color: ${props => props.theme.textGreyTintedLighter};
  margin: 0px;
  font-weight: bold;
  overflow: ${props => (props.active ? 'visible' : 'hidden')};
  border-bottom: '1px solid transparent';
  color: ${props => (props.active ? props.theme.textGreyTintedDarker : props.theme.textGreyTintedLighter)};
  min-width: ${props => (props.active ? '0px' : 'auto')};
  z-index: ${props => (props.active ? '1' : 'auto')};
  margin-bottom: ${props => (props.active ? '-1px' : '0px')};
  background: transparent;
  flex-direction: column;
  cursor: default;
  &:hover {
    color: ${props => props.theme.textGreyTintedDarker};
    ${CodeTabActionsButton} {
      visibility: ${props => (props.active ? 'visible' : 'hidden')};
    }
    ${CodeTabAddItem} {
      display: ${props => (props.active ? 'flex' : 'none')};
    }
  }

  &:last-child {
    > span {
      border-top-right-radius: 3px;
      border-right: 1px solid ${props => props.theme.borderColor};
    }
  }
  &:first-child {
    > span {
      border-left: 1px solid ${props => props.theme.borderColor};
      border-top-left-radius: 3px;
    }
  }
  &:only-child {
    max-width: 100%;
  }
`

const CodeTabTitleContainer = styled.span`
  overflow: ${props => (props.active ? 'visible' : 'hidden')};
  padding: ${props => (props.active ? '0px' : '10px 0px')};
  height: ${props => (props.active ? '30px' : '29px')};
  background: ${props =>
    props.isOver ? props.theme.hoverBg : props.active ? props.theme.bgSecondLightest : props.theme.hoverBgDarkestInverse};
  border-right: 0px;
  border-top: 1px solid ${props => props.theme.borderColor};
  width: 100%;
  text-overflow: ellipsis;
  word-break: normal;
  white-space: pre;
  display: flex;
  align-items: center;
  position: relative;
}
`

const CodeTabTitle = styled.div`
  padding: ${props => (props.active ? (props.readOnly ? '0px 15px 0px 15px' : '0px 15px 0px 0px') : '0px 15px')};
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
`
